import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * B spec §2.3 — **B's full-screen text-entry sheet** (build unit B-6, first consumers: the
 * roster player sheet and the save-template sheet; later units reuse rather than re-make).
 *
 * `<dialog>` + `showModal()` is the floor-safe modal primitive (safari-17-floor.md: 15.4;
 * `popover` light-dismiss is broken until 18.3 and is not used). The mechanics are the
 * platform's one correct shape — the same wiring A's halftime sheet uses, for the same reasons,
 * which is boilerplate the duplication ledger can record as coincidental rather than a clone:
 *
 * - **Opened imperatively.** The `open` attribute renders a *non-modal* dialog that inerts
 *   nothing; `showModal()` is the whole mechanism (top layer + inert page), so it is called
 *   directly on mount rather than mirrored into an attribute.
 * - **One close path.** `cancel` (Escape, platform dismissal) is routed through `onClose`, so
 *   the owner always knows the sheet closed — a dialog the platform closed silently would leave
 *   the owner believing it is open, and the next open would call `showModal()` on an already-
 *   open dialog (an `InvalidStateError`).
 * - **The keyboard rule is structural** (skill §7 forms): the primary action renders in the
 *   sticky header at the TOP of the sheet's own scroll container — the iOS layout viewport
 *   never resizes for the keyboard and `interactive-widget` never shipped, so a bottom-pinned
 *   action would need `visualViewport` tracking; a top action simply cannot be covered. Sticky
 *   inside the scroll container, never `position: fixed` (on iOS `fixed` anchors to the layout
 *   viewport and the keyboard displaces it off-screen).
 *
 * Inerting the page is the right behavior on every B-6 surface: both consumers live in the
 * out-of-match tab shell, where there is no flag bar, no undo strip and no transition control
 * to take away.
 */

export interface SheetBProps {
  readonly titleId: string;
  readonly title: string;
  /** The action row, rendered at the TOP where the soft keyboard cannot cover it. */
  readonly actions: ReactNode;
  readonly onClose: () => void;
  readonly testId: string;
  readonly children: ReactNode;
}

export function SheetB({ titleId, title, actions, onClose, testId, children }: SheetBProps) {
  const dialog = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const element = dialog.current;
    if (element === null || element.open) return;
    element.showModal();
  }, []);

  return (
    <dialog
      ref={dialog}
      className="sheet-b"
      aria-labelledby={titleId}
      data-testid={testId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="sheet-b__scroll">
        <header className="sheet-b__top">
          <h2 className="sheet-b__title" id={titleId}>
            {title}
          </h2>
          <div className="sheet-b__actions">{actions}</div>
        </header>
        <div className="sheet-b__body">{children}</div>
      </div>
    </dialog>
  );
}
